import { rgb2hcb } from "@nova/component-color-finder-legend/src/colorspace"
import { HUE_BINS, hcb2rgb } from "./filter/colorspace"
import { INDEX_LENGTH_U32 } from "../processing/SuperIndexController.class"
import { ColorFinderTiler } from "./ColorFinderTiler.class"
import { ColorFilter } from "./ColorFinderPanel"

export const TIMELINE_ROW_HEIGHT = 20
export const TIMELINE_ROW_MARGIN = 10

const DY = (TIMELINE_ROW_HEIGHT - TIMELINE_ROW_MARGIN) / 2

const { PI, floor, min, clz32 } = Math
const TAU = PI * 2
const MAX_CHROMA = 169

const chromest = new Array<number>(HUE_BINS)
const empty = [0, 0, 0, 0]
const [EMPTY_R, EMPTY_G, EMPTY_B, EMPTY_A] = empty

type CachedColumn = {
    r: Uint8Array
    g: Uint8Array
    b: Uint8Array
    a: Uint8Array
    c: Array<number>
}

let cache = new Map<string, CachedColumn>()

export const clearTimelineCache = () => {
    cache = new Map<string, CachedColumn>()
}

export function drawTimelineV2(
    tiler: ColorFinderTiler,
    start: number,
    end: number,
    canvas: HTMLCanvasElement,
    filter: ColorFilter,
    imageData: ImageData
) {
    const context = canvas.getContext("2d")
    if (!context) return

    const data = imageData.data
    const viewportWidth = imageData.width

    const outArray = new Uint32Array(3)
    const hueMap_R = new Uint8Array(HUE_BINS)
    const hueMap_G = new Uint8Array(HUE_BINS)
    const hueMap_B = new Uint8Array(HUE_BINS)
    const hueMap_A = new Uint8Array(HUE_BINS)

    const HUE_SCALE = HUE_BINS / TAU
    const ALPHA_SCALE = 255 / MAX_CHROMA

    const filterEnabled = filter.size > 0

    // Assumed constants available in scope:
    // clz32, floor, min, TAU, HUE_BINS, MAX_CHROMA, INDEX_LENGTH_U32,
    // chromest, rgb2hcb, TIMELINE_ROW_HEIGHT, EMPTY_R, EMPTY_G,
    // EMPTY_B, EMPTY_A, DY, cache, CachedColumn

    for (let i1 = 0; i1 < viewportWidth; i1++) {
        tiler.convertPixelToTile(i1, start, end, outArray)

        const tileNum = outArray[0]
        const zoom = outArray[1]
        const tileIndex = outArray[2]

        const cacheKey = `${zoom},${tileNum},${tileIndex}`
        let drawBlack = false

        if (cache.has(cacheKey)) {
            const cachedData = cache.get(cacheKey)!
            hueMap_R.set(cachedData.r)
            hueMap_G.set(cachedData.g)
            hueMap_B.set(cachedData.b)
            hueMap_A.set(cachedData.a)
            for (let i = 0; i < HUE_BINS; i++) {
                chromest[i] = cachedData.c[i]
            }
        } else {
            const tile = tiler.getTileByHand(zoom, tileNum)

            if (!tile) {
                drawBlack = true // Set flag instead of continuing
            } else {
                const index = tile.subarray(
                    tileIndex * INDEX_LENGTH_U32,
                    (tileIndex + 1) * INDEX_LENGTH_U32
                )

                chromest.fill(0)

                for (let i = 0; i < index.length; i++) {
                    let word = index[i]
                    while (word) {
                        const bit = 31 - clz32(word)
                        const color = (i << 5) + bit

                        if (filterEnabled && !filter.has(color)) {
                            word ^= 1 << bit
                            continue
                        }

                        const red = (color >> 7) & 0xf8
                        const green = (color >> 2) & 0xf8
                        const blue = (color << 3) & 0xf8

                        const [hue, chroma] = rgb2hcb(color, red, green, blue)
                        const hue_row = floor(hue * HUE_SCALE)

                        if (chroma > chromest[hue_row]) {
                            chromest[hue_row] = chroma
                            const alpha = min(chroma, MAX_CHROMA) * ALPHA_SCALE

                            hueMap_R[hue_row] = red
                            hueMap_G[hue_row] = green
                            hueMap_B[hue_row] = blue
                            hueMap_A[hue_row] = floor(alpha)
                        }
                        word ^= 1 << bit
                    }
                }
                const newCacheData: CachedColumn = {
                    r: new Uint8Array(hueMap_R),
                    g: new Uint8Array(hueMap_G),
                    b: new Uint8Array(hueMap_B),
                    a: new Uint8Array(hueMap_A),
                    c: Array.from(chromest),
                }
                cache.set(cacheKey, newCacheData)
            }
        }

        if (drawBlack) {
            for (let i = 0; i < HUE_BINS; i++) {
                const y = TIMELINE_ROW_HEIGHT * (i + 0.5)
                for (let dy = -DY; dy <= DY; dy++) {
                    const id_index = (floor(y + dy) * viewportWidth + i1) << 2
                    data[id_index + 0] = 32
                    data[id_index + 1] = 32
                    data[id_index + 2] = 32
                    data[id_index + 3] = 255
                }
            }
        } else {
            for (let i = 0; i < HUE_BINS; i++) {
                const y = TIMELINE_ROW_HEIGHT * (i + 0.5)

                let r, g, b, a
                if (chromest[i] === 0) {
                    r = EMPTY_R
                    g = EMPTY_G
                    b = EMPTY_B
                    a = EMPTY_A
                } else {
                    r = hueMap_R[i]
                    g = hueMap_G[i]
                    b = hueMap_B[i]
                    a = hueMap_A[i]
                }

                for (let dy = -DY; dy <= DY; dy++) {
                    const id_index = (floor(y + dy) * viewportWidth + i1) << 2
                    data[id_index + 0] = r
                    data[id_index + 1] = g
                    data[id_index + 2] = b
                    data[id_index + 3] = a
                }
            }
        }
    }

    context.putImageData(imageData, 0, 0)
}

export function drawTimelineLegend(legendDiv: HTMLDivElement) {
    legendDiv.replaceChildren()

    for (let h = 0; h < HUE_BINS; h++) {
        const rgb = hcb2rgb(h / HUE_BINS, 2.0, 0.5)
        const r = rgb[0].toFixed(0)
        const g = rgb[1].toFixed(0)
        const b = rgb[2].toFixed(0)
        const rect = document.createElement("div")
        rect.setAttribute("class", "flex-1 w-3 opacity-50 rounded-sm")
        rect.setAttribute("id", `hue${h}`)
        rect.setAttribute("style", `background-color: rgba(${r}, ${g}, ${b}, 1.0);`)
        legendDiv.appendChild(rect)
    }
}
